"use client"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { ScrollArea } from "@/components/ui/scroll-area"
import {
  Download,
  ExternalLink,
  Heart,
  Share2,
  FileText,
  Video,
  ImageIcon,
  X,
  Loader2,
} from "lucide-react"

interface FilePreviewDrawerProps {
  file: {
    name: string
    type: string
    url: string
    size?: string
    downloadCount?: number
    isLoading?: boolean
  } | null
  isOpen: boolean
  onClose: () => void
}

export function FilePreviewDrawer({
  file,
  isOpen,
  onClose,
}: FilePreviewDrawerProps) {
  if (!file) return null

  const getFileIcon = (type: string) => {
    switch (type) {
      case "pdf":
        return <FileText className="h-5 w-5 text-red-500" />
      case "mp4":
      case "avi":
      case "mov":
        return <Video className="h-5 w-5 text-blue-500" />
      case "png":
      case "jpg":
      case "jpeg":
        return <ImageIcon className="h-5 w-5 text-green-500" />
      default:
        return <FileText className="h-5 w-5 text-gray-500" />
    }
  }

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="right" className="w-full sm:w-[600px] p-0">
        <SheetHeader className="p-4 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {getFileIcon(file.type)}
              <div>
                <SheetTitle className="text-lg">{file.name}</SheetTitle>
                <div className="flex items-center space-x-2 mt-1">
                  <Badge variant="secondary" className="text-xs">
                    {file.type.toUpperCase()}
                  </Badge>
                  {file.size && (
                    <span className="text-xs text-gray-500">{file.size}</span>
                  )}
                </div>
              </div>
            </div>
            <Button size="sm" variant="ghost" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-140px)]">
          <div className="p-4">
            {/* 快速预览区域 */}
            <div className="mb-6">
              <h3 className="font-medium mb-3">文件预览</h3>
              <div className="bg-gray-100 rounded-lg p-4 text-center">
                {file.isLoading || !file.url ? (
                  <div className="space-y-3">
                    <Loader2 className="h-12 w-12 text-blue-500 mx-auto animate-spin" />
                    <p className="text-sm text-gray-600">正在加载预览...</p>
                  </div>
                ) : file.type === "pdf" ? (
                  <div className="space-y-3">
                    <FileText className="h-12 w-12 text-red-500 mx-auto" />
                    <p className="text-sm text-gray-600">PDF 文档预览</p>
                    <Button
                      size="sm"
                      onClick={() => window.open(file.url, "_blank")}
                    >
                      在新标签页中打开
                    </Button>
                  </div>
                ) : file.type === "mp4" ? (
                  <div className="space-y-3">
                    <Video className="h-12 w-12 text-blue-500 mx-auto" />
                    <p className="text-sm text-gray-600">视频文件</p>
                    <Button
                      size="sm"
                      onClick={() => window.open(file.url, "_blank")}
                    >
                      播放视频
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <FileText className="h-12 w-12 text-gray-500 mx-auto" />
                    <p className="text-sm text-gray-600">
                      点击下载查看完整内容
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* 文件信息 */}
            <div className="mb-6">
              <h3 className="font-medium mb-3">文件信息</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">文件名称</span>
                  <span>{file.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">文件类型</span>
                  <span>{file.type.toUpperCase()}</span>
                </div>
                {file.size && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">文件大小</span>
                    <span>{file.size}</span>
                  </div>
                )}
                {file.downloadCount && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">下载次数</span>
                    <span>{file.downloadCount}</span>
                  </div>
                )}
              </div>
            </div>

            {/* 操作按钮 */}
            <div className="space-y-3">
              <Button
                className="w-full"
                onClick={() => window.open(file.url, "_blank")}
              >
                <Download className="h-4 w-4 mr-2" />
                下载文件
              </Button>
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" size="sm">
                  <Heart className="h-4 w-4 mr-2" />
                  收藏
                </Button>
                <Button variant="outline" size="sm">
                  <Share2 className="h-4 w-4 mr-2" />
                  分享
                </Button>
              </div>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => window.open(file.url, "_blank")}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                在新标签页打开
              </Button>
            </div>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  )
}
